import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApiKeys, createApiKey, deleteApiKey } from "../api/users";
import { useAuth } from "../hooks/useAuth";

const ApiKeysPage: React.FC = () => {
  const { t } = useTranslation("apikeys");
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [error, setError] = useState<string>("");

  const { data: apiKeys, isLoading, refetch } = useApiKeys();

  // Проверяем, является ли пользователь администратором
  const isAdmin = user?.roles?.includes("admin") || false;

  const permissions = ["read", "write", "delete", "admin"];

  const handleCreateApiKey = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      try {
        setSaving(true);
        setError("");

        const response = await createApiKey(newKeyName, newKeyPermissions);

        // Обновляем список ключей
        await refetch();

        setShowCreateForm(false);
        setNewKeyName("");
        setNewKeyPermissions([]);

        // Показываем сообщение об успешном создании с возможностью копирования
        alert(
          `${t("modal.title")}\n\n${t("modal.message")}\n\n${t("table.key")}: ${
            response.key
          }`
        );
        copyToClipboard(response.key);
      } catch (err: any) {
        console.error("Error creating API key:", err);
        if (err.response?.status === 401) {
          setError(t("error.unauthorized"));
        } else if (err.response?.status === 403) {
          setError(t("error.access_denied"));
        } else {
          setError(err.message || t("error.create_error"));
        }
      } finally {
        setSaving(false);
      }
    },
    [newKeyName, newKeyPermissions, refetch, t]
  );

  const handleDeleteApiKey = useCallback(
    async (id: number) => {
      if (!window.confirm(t("actions.confirm_delete"))) {
        return;
      }

      try {
        setError("");
        await deleteApiKey(id);
        await refetch();
      } catch (err: any) {
        console.error("Error deleting API key:", err);
        if (err.response?.status === 401) {
          setError(t("error.unauthorized"));
        } else if (err.response?.status === 403) {
          setError(t("error.access_denied"));
        } else {
          setError(err.message || t("error.delete_error"));
        }
      }
    },
    [refetch, t]
  );

  const togglePermission = (permission: string) => {
    setNewKeyPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(text);
      // Сбрасываем сообщение через 2 секунды
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      // Fallback для старых браузеров
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedKey(text);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  // Если пользователь не администратор, показываем сообщение об ошибке
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t("title")}</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("description")}</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t("access_denied")}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {t("access_denied_message")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t("title")}</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("description")}</p>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t("title")}</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t("description")}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {t("actions.add")}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {showCreateForm && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            {t("form.title")}
          </h2>
          <form onSubmit={handleCreateApiKey} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("form.name")}
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full px-3 py-2 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t("form.name_placeholder")}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("form.permissions")}
              </label>
              <div className="space-y-2">
                {permissions.map((permission) => (
                  <label key={permission} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newKeyPermissions.includes(permission)}
                      onChange={() => togglePermission(permission)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t(`permissions.${permission}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving || !newKeyName.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {saving ? t("actions.creating") : t("form.create")}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {t("form.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t("title")}</h2>
        </div>

        {!apiKeys || !Array.isArray(apiKeys) || apiKeys.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
            {t("empty.message")}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {apiKeys.map((apiKey) => (
              <div key={apiKey.id} className="px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {apiKey.name}
                    </h3>
                    <div className="mt-2 mb-2">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("table.key")}:
                        </span>
                        <button
                          onClick={() => {
                            copyToClipboard(apiKey.key);
                          }}
                          className={`text-blue-600 hover:text-blue-800 ${
                            copiedKey === apiKey.key ? "text-green-600" : ""
                          } flex-shrink-0 cursor-pointer`}
                          title={t("modal.copy")}
                        >
                          {copiedKey === apiKey.key ? t("modal.copied") : "📋"}
                        </button>
                      </div>
                      <div
                        className="bg-gray-900 text-gray-100 px-3 py-2 rounded text-sm font-mono w-full cursor-pointer hover:bg-gray-800 hover:text-white"
                        style={{
                          wordBreak: "break-all",
                          overflowWrap: "anywhere",
                          whiteSpace: "pre-wrap",
                          maxWidth: "100%",
                          overflow: "visible",
                        }}
                        title={`${t("table.key")}: ${apiKey.key}`}
                        onClick={() => {
                          alert(`${t("table.key")}:\n\n${apiKey.key}`);
                        }}
                      >
                        {apiKey.key}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-1 sm:space-y-0 text-sm text-gray-500 dark:text-gray-400">
                      <span>
                        {t("table.created")}:{" "}
                        {new Date(apiKey.created_at).toLocaleDateString()}
                      </span>
                      {apiKey.last_used_at && (
                        <span>
                          {t("table.last_used")}:{" "}
                          {new Date(apiKey.last_used_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {apiKey.permissions.map((permission) => (
                        <span
                          key={permission}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {t(`permissions.${permission}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteApiKey(apiKey.id)}
                    className="ml-4 text-red-600 hover:text-red-800"
                  >
                    {t("actions.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeysPage;
